import { Stat } from "@/components/home/panel";
import { RankPanel } from "@/components/home/RankPanel";
import {
  ExceptionPanel,
  MutualLinkNotice,
  PipelinePanel,
  PresencePanel,
  ReportsPanel,
  RosterPanel,
} from "@/pages/home/blocks";
import type { OperationsData } from "@/pages/home/types";

/** The operations manager runs the machine rather than the P&L, so publishing
 *  reliability leads the KPI row. No goal ring — they have never published. */
export function OperationsHome({
  data,
  allowed,
}: {
  data: OperationsData;
  allowed: Set<string>;
}) {
  const goal = data.team.producers ? data.team.goal_today / data.team.producers : 0;
  const p = data.pipeline;
  const total = p.done + p.error + p.cancelled;
  const failPct = Math.round(p.error_rate * 100);
  const mutualNames = data.direct_reports
    .filter((r) => data.mutual_manager_ids.includes(r.id))
    .map((r) => r.full_name || r.email);

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Publish failures"
          value={`${failPct}%`}
          suffix="· 7d"
          bar={p.error_rate}
          tone={failPct >= 20 ? "rose" : failPct >= 10 ? "amber" : "emerald"}
          foot={`${p.error + p.cancelled} of ${total.toLocaleString()} runs`}
          to={allowed.has("/admin/jobs") ? "/admin/jobs" : undefined}
        />
        <Stat
          label="Runs completed"
          value={p.done.toLocaleString()}
          foot="Last 7 days"
          tone="emerald"
        />
        <Stat
          label="On target today"
          value={data.team.on_target}
          suffix={`of ${data.team.producers}`}
          bar={data.team.producers ? data.team.on_target / data.team.producers : undefined}
          tone={data.team.on_target > 0 ? "emerald" : "amber"}
          foot="Brokers at or above the daily goal"
        />
        <Stat
          label="Published today"
          value={data.team.published_today.toLocaleString()}
          foot="Across the whole team"
          to={allowed.has("/placements") ? "/placements" : undefined}
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-3 items-start">
        <div className="lg:col-span-2 min-w-0 space-y-3">
          <ExceptionPanel rows={data.exceptions} />
          <MutualLinkNotice names={mutualNames} />
          <RosterPanel rows={data.roster} goalDaily={goal} />
          <ReportsPanel people={data.direct_reports} />
        </div>
        <div className="space-y-3 min-w-0">
          <PipelinePanel p={p} />
          <PresencePanel p={data.presence} to={allowed.has("/audit") ? "/audit?tab=people" : undefined} />
          <RankPanel />
        </div>
      </div>
    </div>
  );
}
